import { type GetServerSideProps } from 'next';
import {
  PageViewTracker,
  groupActiveRegionByState,
  US_STATES,
  type RegionByState,
} from '@belong/common';
import { Text } from '@belong/ui';
import type { CoverageRegionModel, LicenseSettingsModel } from 'src/api/models';
import { fetchSettingsService } from 'src/api/platform';
import { fetchRegionsService } from 'src/api/region';
import { MetaSEO } from 'src/components/meta-seo/meta-seo';
import { STRINGS } from 'src/features/licenses/strings';
import type { LicenseState } from 'src/features/licenses/types';
import { buildServerClient } from 'src/utils/server-client';

type Props = {
  licenses: LicenseSettingsModel['licenses'];
  activeRegionsByState: RegionByState<CoverageRegionModel>;
};

export default function Licences({ licenses, activeRegionsByState }: Props) {
  const realStateLicenses = Object.keys(activeRegionsByState)
    .map((state) => ({
      state: US_STATES[state as keyof typeof US_STATES].name,
      // `?.` after the index because every state key on `licenses` is
      // optional: a state can be in `activeRegionsByState` without having a
      // licence entry, which this read used to throw on. Falling through to
      // `undefined` is what the `filter` below already expects.
      realStateLicense:
        licenses?.[state as LicenseState]?.RealEstate?.belongLicenseNumber,
    }))
    .filter(({ realStateLicense }) => Boolean(realStateLicense));

  const insuranceLicenses = Object.keys(activeRegionsByState)
    .map((state) => ({
      state: US_STATES[state as keyof typeof US_STATES].name,
      insuranceLicense:
        licenses?.[state as LicenseState]?.Insurance?.belongLicenseNumber,
    }))
    .filter(({ insuranceLicense }) => Boolean(insuranceLicense));

  return (
    <PageViewTracker name="insurances">
      <MetaSEO
        title={STRINGS.metaTitle}
        description={STRINGS.metaDescription}
        openGraph={{
          title: STRINGS.metaTitle,
          description: STRINGS.metaDescription,
        }}
      />
      <section className="my-2xl container flex flex-col md:flex-row">
        <div className="mb-xl gap-sm flex flex-1 flex-col md:mb-0">
          <Text variant="h2" fontWeight="semibold">
            {STRINGS.realStateLicenses}
          </Text>
          <Text className="md:font-regular font-semibold">
            {STRINGS.realStateLicensesDescription}
          </Text>
        </div>
        <div className="gap-3xl md:gap-2xs flex w-full flex-1 md:justify-evenly">
          <div className="gap-2xs flex flex-col">
            <Text fontWeight="semibold">{STRINGS.state}</Text>
            {realStateLicenses.map(({ state }) => (
              <Text variant="p1" key={state}>
                {state}
              </Text>
            ))}
          </div>
          <div className="gap-2xs flex flex-col">
            <Text fontWeight="semibold">{STRINGS.licenseNo}</Text>
            {realStateLicenses.map(({ state, realStateLicense }) => (
              <Text variant="p1" key={state}>
                {realStateLicense}
              </Text>
            ))}
          </div>
        </div>
      </section>
      <section className="my-2xl container flex flex-col md:flex-row">
        <div className="mb-xl gap-sm flex flex-1 flex-col md:mb-0">
          <Text variant="h2" fontWeight="semibold">
            {STRINGS.insuranceLicenses}
          </Text>
          <Text className="md:font-regular font-semibold">
            {STRINGS.insuranceLicensesDescription}
          </Text>
        </div>
        <div className="gap-3xl md:gap-2xs flex w-full flex-1 md:justify-evenly">
          <div className="gap-2xs flex flex-col">
            <Text fontWeight="semibold">{STRINGS.state}</Text>
            {insuranceLicenses.map(({ state }) => (
              <Text variant="p1" key={state}>
                {state}
              </Text>
            ))}
          </div>
          <div className="gap-2xs flex flex-col">
            <Text fontWeight="semibold">{STRINGS.licenseNo}</Text>
            {insuranceLicenses.map(({ state, insuranceLicense }) => (
              <Text variant="p1" key={state}>
                {insuranceLicense}
              </Text>
            ))}
          </div>
        </div>
      </section>
    </PageViewTracker>
  );
}

export const getServerSideProps: GetServerSideProps<Props> = async (
  context,
) => {
  const apiClient = buildServerClient(context);

  const [regions, settings] = await Promise.all([
    fetchRegionsService(apiClient),
    fetchSettingsService(),
  ]);

  const activeRegionsByState = groupActiveRegionByState(regions);
  const licenses = settings?.licenseSettings?.licenses;

  return {
    props: {
      licenses,
      activeRegionsByState,
    },
  };
};
